import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { MdAdd } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type {
  GetContextMenuItemsParams,
  GetRowIdParams,
  IServerSideGetRowsParams,
  MenuItemDef,
  RowClickedEvent,
} from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, SyntheticEvent } from 'react'
import type { ICluster, IListClustersParams } from '~/types/cluster'

import columns from './columns'
import MetricsBlock from './MetricsBlock'

import CreateClusterModal from '../modals/CreateClusterModal'
import EditClusterTagsModal from '../modals/EditClusterTagsModal'

import { listClusters } from '~/api/clusters'
import Button from '~/components/Buttons/Button'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import { CLUSTER_HARDCODED_TAGS } from '~/const/clusters'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { keys as clusterKeys, useClustersTagsQuery, useSetClusterTagsMutation } from '~/queries/clusters'
import { debounce } from '~/utils/function'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const CACHE_BLOCK_SIZE = 50

const ENTRIES_NAME = 'competitors-clusters'

const Clusters = (): ReactElement => {
  /** Local state */

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<ICluster>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [clusterForTagsEdit, setClusterForTagsEdit] = useState<ICluster>()

  const [isCreateClusterModalOpen, setIsCreateClusterModalOpen] = useState(false)

  const [isEditClusterTagsModalOpen, setIsEditClusterTagsModalOpen] = useState(false)

  const [search, setSearch] = useState<string>()

  const debouncedSearch = debounce((search) => setSearch(search), 500)

  const datasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        const { api, fail, request, success } = params

        api.hideOverlay()

        const currentPage = (request.startRow || 0) / CACHE_BLOCK_SIZE

        const queryParams: IListClustersParams = {
          orderBy: request.sortModel.length ? `${request.sortModel[0].colId}+${request.sortModel[0].sort}` : undefined,
          page: currentPage,
          q: search,
          size: CACHE_BLOCK_SIZE,
        }

        const { data } = await queryClient.fetchQuery({
          queryFn: () => listClusters(queryParams),
          queryKey: clusterKeys.listClusters(queryParams),
        })

        if (data) {
          if (!data.length) api.showNoRowsOverlay()
          success({ rowData: data })
        } else {
          fail()
        }
      },
    }),
    [queryClient, search]
  )

  /** Queries & Mutations */

  const { data: tagsData } = useClustersTagsQuery()

  const { mutateAsync: setClusterTags } = useSetClusterTagsMutation()

  const editClusterTags = ({ clusterId, tags }: { clusterId: string; tags: string[] }) => {
    const rowData = gridRef.current?.api.getRowNode(clusterId)?.data

    if (rowData) {
      gridRef.current.api.applyServerSideTransaction({ update: [{ ...rowData, tags }] })
      displayToast('Tags successfully updated', 'success')
    }
  }

  /** Effects */

  useDocumentTitle('Usage Clustering - Clusters')

  const handleGetRowId = useCallback(({ data }: GetRowIdParams<ICluster>) => data.cluster_id, [])

  const handleSearchChange = (e: SyntheticEvent<HTMLInputElement>) => debouncedSearch(e.currentTarget.value)

  const handleRowClicked = useCallback(
    (evt: RowClickedEvent<ICluster>) => {
      if (evt.data?.cluster_id) navigate(`/competitors/clusters/${evt.data.cluster_id}`)
    },
    [navigate]
  )

  const handleCreateClusterModalState = (isOpen: boolean) => () => setIsCreateClusterModalOpen(isOpen)

  const handleEditClusterTagsModalState = (isOpen: boolean) => () => setIsEditClusterTagsModalOpen(isOpen)

  const handleCreateClusterSuccess = () => {
    gridRef.current?.api.setGridOption('serverSideDatasource', datasource)
    setIsCreateClusterModalOpen(false)
    displayToast('Cluster successfully created', 'success')
  }

  const handleAddTag = useCallback(
    (rowData: ICluster, tag: string) => async () => {
      const { cluster_id, tags = [] } = rowData
      const newTags = tags ? [...tags, tag] : [tag]

      try {
        await setClusterTags({ clusterId: cluster_id, tags: newTags })
        editClusterTags({ clusterId: cluster_id, tags: newTags })
      } catch (e) {
        captureException(e)
        displayToast(`Tag could not be added`, 'error')
      }
    },
    [setClusterTags]
  )

  const handleEditTags = (cluster: ICluster) => () => {
    setClusterForTagsEdit(cluster)
    setIsEditClusterTagsModalOpen(true)
  }

  const handleEditTagSuccess = (data: { clusterId: string; tags: string[] }) => {
    editClusterTags(data)
    setIsEditClusterTagsModalOpen(false)
  }

  const getAddTagActions = useCallback(
    (rowData: ICluster): MenuItemDef[] => {
      const menuItems: MenuItemDef[] = []

      for (const { icon, label, value } of CLUSTER_HARDCODED_TAGS) {
        if (!rowData.tags || !rowData.tags.includes(value)) {
          menuItems.push({
            action: handleAddTag(rowData, value),
            cssClasses: ['menu-item'],
            icon,
            name: `Tag as "${label}"`,
          })
        }
      }

      return menuItems
    },
    [handleAddTag]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<ICluster>) => {
      const rowData = params.node?.data

      if (!rowData) return []

      const menuItems: MenuItemDef[] = [
        {
          action: handleEditTags(rowData),
          cssClasses: ['menu-item'],
          icon: '🏷️',
          name: 'Update tags',
        },
        ...getAddTagActions(rowData),
      ]

      return menuItems
    },
    [getAddTagActions]
  )

  /** Render */

  return (
    <div className="clusters-listing-view">
      <Section>
        <MetricsBlock />
        <ServerSideTableWrapper
          buttonComponent={
            <div className="competitors-clusters-view">
              <Button onClick={handleCreateClusterModalState(true)}>
                <MdAdd color="#fff" size={16} /> Create cluster
              </Button>
            </div>
          }
          cacheBlockSize={CACHE_BLOCK_SIZE}
          columnDefs={columns}
          entriesName={ENTRIES_NAME}
          getContextMenuItems={getContextMenuItems}
          getRowId={handleGetRowId}
          onRowClicked={handleRowClicked}
          onSearchChange={handleSearchChange}
          ref={gridRef}
          rowHeight={95}
          rowModelType="serverSide"
          searchPlaceholder="Enter a Cluster name or ID"
          serverSideDatasource={datasource}
          suppressCsvExport
          suppressExcelExport
          tableRef={tableRef}
          withPagination={false}
          withSearchBar
        />
      </Section>
      <CreateClusterModal
        clusterTags={tagsData || []}
        isOpen={isCreateClusterModalOpen}
        onClose={handleCreateClusterModalState(false)}
        onSuccess={handleCreateClusterSuccess}
      />
      {clusterForTagsEdit ? (
        <EditClusterTagsModal
          cluster={clusterForTagsEdit}
          clusterTags={tagsData || []}
          isOpen={isEditClusterTagsModalOpen}
          onClose={handleEditClusterTagsModalState(false)}
          onSuccess={handleEditTagSuccess}
        />
      ) : null}
    </div>
  )
}

export default Clusters
